import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import type { ApprovalStatus, Role } from "@/types";

// ============================================================================
// AUTENTICACIÓN (Supabase Auth)
// Maneja sesión, usuario, perfil (rol admin/asesor) y registro de asesores.
// El primer usuario registrado se convierte en Administrador (Juanka).
// ============================================================================

const PENDING_KEY = "attd_pending_profile";

export interface ProfileInfo {
  id: string;
  role: Role;
  approvalStatus: ApprovalStatus;
  names: string;
  surnames: string;
  cedula: string;
  phone: string;
  email: string;
  city: string;
  margin: 20 | 25;
  consignmentLimit: number;
}

export interface RegisterPayload {
  names: string;
  surnames: string;
  cedula: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  margin: 20 | 25;
  consignmentLimit: number;
  refName: string;
  refRelationship: string;
  refPhone: string;
  refCity: string;
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: ProfileInfo | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    payload: RegisterPayload
  ) => Promise<{ needsConfirmation: boolean }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

type Row = Record<string, unknown>;

const mapProfile = (r: Row): ProfileInfo => ({
  id: String(r.id),
  role: String(r.role) === "admin" ? "admin" : "asesor",
  approvalStatus:
    String(r.approval_status) === "pending" ||
    String(r.approval_status) === "rejected" ||
    String(r.approval_status) === "suspended"
      ? (String(r.approval_status) as ApprovalStatus)
      : "approved",
  names: String(r.names ?? ""),
  surnames: String(r.surnames ?? ""),
  cedula: String(r.cedula ?? ""),
  phone: String(r.phone ?? ""),
  email: String(r.email ?? ""),
  city: String(r.city ?? ""),
  margin: Number(r.margin) === 20 ? 20 : 25,
  consignmentLimit: Number(r.consignment_limit ?? 0),
});

const fetchProfile = async (uid: string): Promise<ProfileInfo | null> => {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", uid)
    .maybeSingle();
  if (error || !data) return null;
  return mapProfile(data as Row);
};

const ensureProfile = async (uid: string, email: string): Promise<ProfileInfo | null> => {
  const existing = await fetchProfile(uid);
  if (existing) return existing;

  let payload: RegisterPayload | null = null;
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (raw) payload = JSON.parse(raw) as RegisterPayload;
  } catch {
    payload = null;
  }

  const p: RegisterPayload = payload ?? {
    names: email.split("@")[0] || "Usuario",
    surnames: "",
    cedula: "",
    phone: "",
    email,
    address: "",
    city: "",
    margin: 25,
    consignmentLimit: 0,
    refName: "",
    refRelationship: "",
    refPhone: "",
    refCity: "",
  };

  const { data, error } = await supabase.rpc("register_my_profile", {
    p_names: p.names,
    p_surnames: p.surnames,
    p_cedula: p.cedula,
    p_phone: p.phone,
    p_email: p.email || email,
    p_address: p.address,
    p_city: p.city,
    p_margin: p.margin,
    p_consignment_limit: p.consignmentLimit,
    p_ref_name: p.refName,
    p_ref_relationship: p.refRelationship,
    p_ref_phone: p.refPhone,
    p_ref_city: p.refCity,
  });
  if (error || !data) return null;
  localStorage.removeItem(PENDING_KEY);
  return mapProfile(data as Row);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ProfileInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      return;
    }
    const p = await ensureProfile(user.id, user.email ?? "");
    setProfile(p);
  }, [user]);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }
    let active = true;
    ensureProfile(user.id, user.email ?? "").then((p) => {
      if (active) setProfile(p);
    });
    return () => {
      active = false;
    };
  }, [user]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, payload: RegisterPayload) => {
      localStorage.setItem(PENDING_KEY, JSON.stringify({ ...payload, email }));
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { names: payload.names, surnames: payload.surnames, role: "asesor" },
        },
      });
      if (error) throw new Error(error.message);
      return { needsConfirmation: !data.session };
    },
    []
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setProfile(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ session, user, profile, loading, signIn, signUp, signOut, refreshProfile }),
    [session, user, profile, loading, signIn, signUp, signOut, refreshProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
};